import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Ticket, Bell, BellOff, QrCode, Gift, Loader2, AlertTriangle, ShieldCheck, Flame } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView, trackEvent, flushEvents } from '../lib/analytics';

interface Coupon {
  id: number;
  redeemCode: string;
  status: 'claimed' | 'redeemed' | 'expired';
  claimedAt: number;
  redeemedAt?: number | null;
  offerTitle?: string | null;
  restaurantName?: string | null;
  validUntil?: number | null;
  offerKey?: string | null;
  imageUrl?: string | null;
}

interface MerchantToggle {
  id: number;
  name: string;
  consent: boolean;
}

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  claimed: { label: '待使用', cls: 'bg-amber-100 text-amber-800' },
  redeemed: { label: '已兌換', cls: 'bg-green-100 text-green-700' },
  expired: { label: '已過期', cls: 'bg-stone-200 text-stone-500' },
};

export default function MyCenter() {
  const { code = '' } = useParams<{ code: string }>();
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [merchants, setMerchants] = useState<MerchantToggle[]>([]);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await fetch(`/api/public/me/session?token=${encodeURIComponent(code)}`);
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '連結無效');
      setCoupons(Array.isArray(data.coupons) ? data.coupons : []);
      setMerchants(Array.isArray(data.merchants) ? data.merchants : []);
      setUnsubscribed(!!data.unsubscribed);
      setState('ready');
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '連結無效');
      setState('error');
    }
  };

  useEffect(() => {
    trackPageView('/me');
    if (!code) { setState('error'); setError('缺少連結參數'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const toggleConsent = async (m: MerchantToggle) => {
    setBusy(`m-${m.id}`);
    try {
      const res = await fetch('/api/public/me/consent', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: code, merchantId: m.id, on: !m.consent }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '更新失敗');
      setMerchants((cur) => cur.map((x) => (x.id === m.id ? { ...x, consent: !m.consent } : x)));
      trackEvent('marketing_consent_toggle', { merchantId: m.id, on: !m.consent ? 1 : 0, via: 'my_center' });
      flushEvents();
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新失敗');
    } finally {
      setBusy(null);
    }
  };

  const toggleUnsub = async () => {
    setBusy('unsub');
    try {
      const res = await fetch('/api/public/me/unsubscribe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: code, on: !unsubscribed }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '更新失敗');
      setUnsubscribed(!!data.unsubscribed);
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新失敗');
    } finally {
      setBusy(null);
    }
  };

  const nowSec = Date.now() / 1000;

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />

      <section className="relative bg-x1493-dark text-white py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">我的優惠中心</h1>
          <p className="text-white/80">一頁管理你嘅優惠券同通知設定 · 建議收藏呢個頁面</p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-3xl mx-auto px-4 space-y-8">
          {state === 'loading' && <div className="text-center py-20 text-stone-500">載入中...</div>}

          {state === 'error' && (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
              <AlertTriangle className="mx-auto text-amber-500 mb-4" size={48} />
              <p className="text-stone-700 font-bold mb-2">{error}</p>
              <p className="text-stone-500 text-sm">連結可能輸入不正確。經 WhatsApp 重新領取任何優惠即可獲得你的專屬連結。</p>
            </div>
          )}

          {state === 'ready' && (
            <>
              {/* Coupons */}
              <div>
                <h2 className="font-bold text-lg text-stone-900 mb-3 flex items-center gap-2">
                  <Ticket size={18} className="text-x1493-primary" /> 我的優惠券（{coupons.length}）
                </h2>
                {coupons.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-stone-500">
                    未有優惠券——去 <a href="/offers" className="text-x1493-primary font-bold hover:underline">食肆優惠</a> 睇吓有咩著數！
                  </div>
                ) : (
                  <div className="space-y-3">
                    {coupons.map((cp) => {
                      const st = STATUS_LABEL[cp.status] || STATUS_LABEL.claimed;
                      const isExpiredByDate = !!cp.validUntil && cp.validUntil > 0 && cp.validUntil < nowSec;
                      return (
                        <div key={cp.id} className="bg-white rounded-2xl border border-stone-200 p-4 flex items-center gap-4">
                          <div className="w-16 h-16 rounded-xl overflow-hidden bg-stone-100 flex items-center justify-center shrink-0">
                            {cp.imageUrl ? <img src={cp.imageUrl} alt={cp.restaurantName || ''} className="w-full h-full object-cover" />
                              : <Gift size={24} className="text-stone-300" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-stone-900 truncate">{cp.restaurantName || '餐廳優惠'}</p>
                            <p className="text-sm text-stone-500 truncate">{cp.offerTitle}</p>
                            <p className="text-xs text-stone-400 mt-0.5">
                              {cp.validUntil ? `有效期至 ${new Date(cp.validUntil * 1000).toLocaleDateString('zh-HK')}` : '長期有效'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2 shrink-0">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>
                              {isExpiredByDate && cp.status === 'claimed' ? '已過期' : st.label}
                            </span>
                            {cp.status === 'claimed' && !isExpiredByDate && (
                              <a href={`/redeem/${cp.redeemCode}`} className="inline-flex items-center gap-1 text-xs font-bold text-x1493-primary hover:underline">
                                <QrCode size={13} /> 出示 QR
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Consent toggles */}
              {merchants.length > 0 && (
                <div className="bg-white rounded-2xl border border-stone-200 p-6">
                  <h2 className="font-bold text-lg text-stone-900 mb-1 flex items-center gap-2">
                    <Bell size={18} className="text-x1493-primary" /> 優惠通知設定
                  </h2>
                  <p className="text-xs text-stone-500 mb-4">開啟後，該餐廳有新優惠時可以透過 WhatsApp 通知你。隨時關閉，唔影響你使用已領取嘅優惠。</p>
                  <ul className="divide-y divide-stone-100">
                    {merchants.map((m) => (
                      <li key={m.id} className="py-3 flex items-center justify-between gap-3">
                        <span className="font-medium text-stone-800 text-sm">{m.name}</span>
                        <button
                          onClick={() => toggleConsent(m)}
                          disabled={busy === `m-${m.id}` || unsubscribed}
                          title={unsubscribed ? '你已全部拒收，請先關閉全局拒收' : undefined}
                          className={`relative w-12 h-6 rounded-full transition shrink-0 disabled:opacity-40 ${m.consent ? 'bg-x1493-primary' : 'bg-stone-300'}`}
                          aria-label={`收到${m.name}的優惠資訊`}
                        >
                          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${m.consent ? 'left-6' : 'left-0.5'}`} />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-4 border-t border-stone-100">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-1.5 text-sm font-bold text-stone-700">
                        <BellOff size={15} className={unsubscribed ? 'text-red-500' : 'text-stone-400'} />
                        全部拒收（唔收香港辣谷任何推廣訊息）
                      </span>
                      <button
                        onClick={toggleUnsub}
                        disabled={busy === 'unsub'}
                        className={`relative w-12 h-6 rounded-full transition shrink-0 disabled:opacity-40 ${unsubscribed ? 'bg-red-500' : 'bg-stone-300'}`}
                        aria-label="全部拒收"
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${unsubscribed ? 'left-6' : 'left-0.5'}`} />
                      </button>
                    </div>
                    {unsubscribed && <p className="text-xs text-red-500 mt-1.5">已開啟全部拒收——訂單必要的訊息（如付款確認）仍會發送。</p>}
                  </div>
                  <p className="text-[10px] text-stone-400 mt-4 flex items-center gap-1">
                    <ShieldCheck size={11} /> 你的選擇會以時間記錄，詳情見
                    <a href="/privacy" className="underline hover:text-x1493-primary">隱私條款</a>
                  </p>
                </div>
              )}

              {/* Explore more */}
              <div className="bg-x1493-dark rounded-2xl p-6 text-center">
                <Flame className="mx-auto text-x1493-secondary mb-2" size={24} />
                <p className="text-white font-bold mb-1">仲有更多餐廳優惠等緊你</p>
                <a href="/offers" className="inline-block mt-2 px-6 py-2.5 bg-x1493-primary text-white rounded-xl font-bold hover:bg-red-700 transition">
                  探索食肆優惠 →
                </a>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
